import {
  type ReactNode,
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { CommandPalette } from './CommandPalette';

interface CommandPaletteState {
  open: () => void;
}

const CommandPaletteContext = createContext<CommandPaletteState | null>(null);

/**
 * Owns the palette's open state and the global shortcut.
 *
 * `Ctrl+K` / `Cmd+K` is the near-universal binding, with `Ctrl+/` as an alias
 * because some browsers and extensions claim Ctrl+K for the address bar. Both
 * carry a modifier, which is what WCAG 2.1.4 requires of a global shortcut —
 * a bare single-key binding would capture typing anywhere in the app.
 */
export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      const modifier = event.metaKey || event.ctrlKey;
      if (!modifier) return;

      const key = event.key.toLowerCase();
      if (key === 'k' || key === '/') {
        event.preventDefault();
        // Toggle, so the same keystroke that opens it closes it.
        setIsOpen((current) => !current);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const value = useMemo<CommandPaletteState>(() => ({ open }), [open]);

  return (
    <CommandPaletteContext value={value}>
      {children}
      <CommandPalette open={isOpen} onClose={close} />
    </CommandPaletteContext>
  );
}

export function useCommandPalette(): CommandPaletteState {
  const context = use(CommandPaletteContext);
  if (!context) {
    throw new Error('useCommandPalette must be used inside <CommandPaletteProvider>');
  }
  return context;
}
